import { Formik } from 'formik'
import { isEmpty, isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { defaultMemoize } from 'reselect'

import Card from '@/components/Card'
import { AutoComplete, Select } from '@/components/Form'
import { Person } from '@/components/Icons'
import Page from '@/components/Page'
import { fetchRoles, resetRequests } from '@/store/actions'
import { getSpaceRoles, isRequestComplete, isRequestPending } from '@/store/selectors'

import {
  addUserToRole,
  fetchSpaceMembersInRole,
  fetchSpaceMembersNotInRole,
  removeUserFromRole,
  resetPaginationState
} from './actions'
import {
  ADD_SPACE_MEMBER_FROM_ROLE,
  FETCH_SPACE_MEMBERS_BY_NOT_ROLE,
  FETCH_SPACE_MEMBERS_BY_ROLE,
  REMOVE_SPACE_MEMBER_FROM_ROLE
} from './actionTypes'
import messages from './messages'
import {
  getSelectedRoleGuid,
  getSortBy,
  getStartIndex,
  getTotalItemsCount,
  mapPaginatedUsers,
  mapSearchUsers
} from './selectors'
import UsersByRoleTable from './UsersByRoleTable'

import './index.scss'

import filterSpaceRoles from '@/components/RolesContainer/filterSpaceRoles'
import useFeatures from '@/components/useFeatures'
import { USER_ATTRIBUTES } from '@/components/User'

const MEMBERS_PER_PAGE = 10

function SpaceMembersRolesPage({
  actions,
  direction,
  isAddComplete,
  isLoading,
  isSearching,
  isRemoveComplete,
  isSearchUsersComplete,
  searchResults,
  selectedRoleGuid,
  spaceRoles,
  startIndex,
  totalItemsCount,
  usersByRole,
  orgAdminRole,
  sortBy
}: any) {
  const intl = useIntl()

  const sortOrders = {
    ASC: 'ascending',
    DESC: 'descending'
  }

  const features = useFeatures()

  const [selectedRoleGuidState, setSelectedRoleGuid] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResultsState, setSearchResults] = useState([])

  useEffect(() => {
    if (isEmpty(orgAdminRole)) {
      actions.fetchRoles()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgAdminRole])

  useEffect(() => {
    if (selectedRoleGuid && selectedRoleGuidState === null) {
      setSelectedRoleGuid('')
      setSearchTerm('')
      setSearchResults([])
    }
  }, [selectedRoleGuidState, selectedRoleGuid])

  useEffect(() => {
    if (isSearchUsersComplete && !isSearching && !isEqual(searchResultsState, searchResults)) {
      setSearchResults(searchResults)
    }
  }, [isSearchUsersComplete, isSearching, searchResultsState, searchResults])

  useEffect(() => {
    if (isAddComplete || isRemoveComplete) {
      actions.resetRequests([ADD_SPACE_MEMBER_FROM_ROLE, REMOVE_SPACE_MEMBER_FROM_ROLE])
      loadSpaceMembersByRole(1, sortBy, direction)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddComplete, isRemoveComplete])

  const generateSearchResults = defaultMemoize((searchTerm, roleGuid, users) => {
    if (searchTerm.length === 0 || !roleGuid) {
      return []
    }

    return users.map((user: any) => ({
      label: user.displayName,
      leftIcon: <Person />,
      subLabel: user.email,
      value: user.id,
      id: user.id
    }))
  })

  /**
   * Load the users given current sorting and the state of the selected role guid
   *
   * @param page the current page number requested (default 1)
   * @param sortBy the column to sort by
   * @param direction the direction to sort
   */
  const loadSpaceMembersByRole = (page = 1, sortBy: any, direction: any, guid?: string) => {
    actions.fetchSpaceMembersInRole(
      guid || selectedRoleGuidState,
      page,
      MEMBERS_PER_PAGE,
      sortBy,
      // @ts-expect-error
      sortOrders[direction || '']
    )
  }

  /**
   * User clicked the member in autocomplete results
   */
  const onAddRoleToUser = (value: any) => {
    // Reset the input to the search box
    setSearchTerm('')
    actions.addUserToRole(value.id, selectedRoleGuidState)
    return { display: '', value: '' }
  }

  /**
   * The user changed the role filter drop down
   *
   * @param e the event from the select drop down
   */
  const onFilterRoleChange = (e: any) => {
    setSelectedRoleGuid(e.target.value)
    loadSpaceMembersByRole(1, sortBy, direction, e.target.value)
  }

  const onSearchChange = (value: any) => {
    value = value.trim()

    //this.setState({ searchTerm: value })
    setSearchTerm(value)

    // Don't search if the user enters in spaces
    if (value.length === 0) {
      return
    }

    const query = {
      attributes: USER_ATTRIBUTES,
      count: MEMBERS_PER_PAGE,
      filter: USER_ATTRIBUTES.map(attribute => `${attribute} co ${JSON.stringify(value)}`).join(' or '),
      inverse: true
    }

    return actions.fetchSpaceMembersNotInRole(selectedRoleGuidState, query)
  }

  /**
   * Remove a user from the selected role guid from state
   *
   * @param userId the user's GUID
   */
  const removeRole = (userId: any) => {
    actions.removeUserFromRole(userId, selectedRoleGuidState)
  }

  const renderItem = ({ label, leftIcon, subLabel, value }: any) => {
    return (
      <div key={value} className="space-member-result">
        {!!leftIcon && <div className="result__left">{leftIcon}</div>}
        <div className="result__right">
          <p>{label}</p>
          {!!subLabel && <p>{subLabel}</p>}
        </div>
      </div>
    )
  }

  spaceRoles = filterSpaceRoles(spaceRoles, features, true)

  return (
    <Page className="space-members">
      <Page.Header title={<FormattedMessage {...messages.title} />} />
      <Card isActionInProgress={isLoading}>
        <Card.Content>
          <div>
            {/* @ts-expect-error */}
            <Formik initialValues={{ filterRoles: selectedRoleGuidState }} enableReinitialize>
              <>
                <Select
                  name="filterRoles"
                  label={<FormattedMessage {...messages.selectRole} />}
                  onChange={onFilterRoleChange}
                  value={selectedRoleGuidState}
                >
                  <>
                    {!selectedRoleGuidState && (
                      <Select.Option key={-1} value="">
                        {intl.formatMessage(messages.blank)}
                      </Select.Option>
                    )}
                    {spaceRoles.map((spaceRole: any) => {
                      const checkBox = (
                        <Select.Option key={spaceRole.name} value={spaceRole.id}>
                          {spaceRole.displayName}
                        </Select.Option>
                      )

                      return checkBox
                    })}
                  </>
                </Select>

                {selectedRoleGuidState && selectedRoleGuidState.length > 0 && (
                  <AutoComplete
                    className="user-search_autocomplete"
                    key="spaceRoleAdd"
                    name="spaceRoleAdd"
                    label={<FormattedMessage {...messages.addMember} />}
                    placeholder={intl.formatMessage(messages.searchPlaceholder)}
                    onResultClick={onAddRoleToUser}
                    onSearchChange={onSearchChange}
                    renderResult={renderItem}
                    isSearching={isSearching}
                    results={generateSearchResults(searchTerm, selectedRoleGuidState, searchResultsState)}
                  />
                )}
              </>
            </Formik>
          </div>

          {selectedRoleGuidState && selectedRoleGuidState.length > 0 && (
            <UsersByRoleTable
              loadSpaceMembersByRole={loadSpaceMembersByRole}
              removeRole={removeRole}
              perPage={MEMBERS_PER_PAGE}
              startIndex={startIndex}
              memberCount={totalItemsCount}
              members={usersByRole}
              sortBy={sortBy}
              direction={direction}
              isLoading={isLoading}
            />
          )}
        </Card.Content>
      </Card>
    </Page>
  )
}

SpaceMembersRolesPage.displayName = 'SpaceMembersRolesPage'

const DisconnectedSpaceMembersRolesPage = SpaceMembersRolesPage
export { /** @deprecated */ DisconnectedSpaceMembersRolesPage as SpaceMembersRolesPage }

const mapStateToProps = (state: any) => ({
  direction: state.spaceMembersRolesPage.direction,

  isLoading: isRequestPending(state, [
    ADD_SPACE_MEMBER_FROM_ROLE,
    FETCH_SPACE_MEMBERS_BY_ROLE,
    REMOVE_SPACE_MEMBER_FROM_ROLE
  ]),

  isAddComplete: isRequestComplete(state, ADD_SPACE_MEMBER_FROM_ROLE),
  isRemoveComplete: isRequestComplete(state, REMOVE_SPACE_MEMBER_FROM_ROLE),
  isSearching: isRequestPending(state, FETCH_SPACE_MEMBERS_BY_NOT_ROLE),
  isSearchUsersComplete: isRequestComplete(state, FETCH_SPACE_MEMBERS_BY_NOT_ROLE),
  searchResults: mapSearchUsers(state),
  selectedRoleGuid: getSelectedRoleGuid(state),
  sortBy: getSortBy(state),
  spaceRoles: getSpaceRoles(state),
  startIndex: getStartIndex(state),
  totalItemsCount: getTotalItemsCount(state),
  usersByRole: mapPaginatedUsers(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      addUserToRole,
      fetchRoles,
      fetchSpaceMembersInRole,
      fetchSpaceMembersNotInRole,
      removeUserFromRole,
      resetPaginationState,
      resetRequests
    },
    dispatch
  )
})

SpaceMembersRolesPage.propTypes = {
  actions: PropTypes.shape({
    addUserToRole: PropTypes.func.isRequired,
    fetchRoles: PropTypes.func.isRequired,
    fetchSpaceMembersInRole: PropTypes.func.isRequired,
    fetchSpaceMembersNotInRole: PropTypes.func.isRequired,
    removeUserFromRole: PropTypes.func.isRequired,
    resetPaginationState: PropTypes.func.isRequired,
    resetRequests: PropTypes.func.isRequired
  }).isRequired,
  direction: PropTypes.string,
  isAddComplete: PropTypes.bool.isRequired,
  isRemoveComplete: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSearching: PropTypes.bool.isRequired,
  isSearchUsersComplete: PropTypes.bool.isRequired,
  searchResults: PropTypes.array,
  selectedRoleGuid: PropTypes.string,
  sortBy: PropTypes.string,
  spaceRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      displayName: PropTypes.string,
      sortOrder: PropTypes.number
    })
  ),
  startIndex: PropTypes.number,
  totalItemsCount: PropTypes.number,
  usersByRole: PropTypes.array
}

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedSpaceMembersRolesPage)
